import { type MutationMeta, queryOptions, skipToken, useMutation, useQuery } from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { useActiveOrganizationId } from '@/features/auth/useSession';
import { useUploadBrandHubFile } from '@/features/brandHub/assets/useBrandAssets';
import { brandHubQueryKey, byName } from '@/features/brandHub/brandHub';
import { useObjectUrlQuery } from '@/features/organization/useOrganization';
import { type LiveCollection, useLiveCollection, useLiveMutation } from '@/hooks/useLiveData';
import { apiClient } from '@/lib/apiClient';
import type { PendingKind } from '@/lib/pendingWrites';
import { parseWriteResponse, readRecord, requireParsed } from '@/lib/readers';
import { assembleTemplatePreview, type PreviewResult } from './templateArchive';
import {
  parseTemplate,
  parseTemplateInspection,
  type Template,
  type TemplateInspection,
  type TemplateSize,
  type TemplateSource,
  type TemplateType,
} from './types';

const TEMPLATE_LIMIT = 200;

/** Returns every template of the organization, sorted by name. */
export function useTemplates(): LiveCollection<Template> {
  const organizationId = useActiveOrganizationId();

  const collection = useLiveCollection<Template>({
    path: collections.templates(organizationId ?? '_'),
    httpPath: endpoints.brandHub.templates(),
    queryKey: brandHubQueryKey('templates', organizationId),
    parse: parseTemplate,
    limit: TEMPLATE_LIMIT,
    enabled: Boolean(organizationId),
  });

  return { ...collection, data: byName(collection.data, (template) => template.name) };
}

/** Returns the templates of one type whose name contains the query. */
export function useFilteredTemplates(templates: Template[], templateType: TemplateType, query: string): Template[] {
  const needle = query.trim().toLowerCase();
  return templates.filter((template) => template.templateType === templateType && template.name.toLowerCase().includes(needle));
}

export interface TemplateCreate {
  name: string;
  templateType: TemplateType;
  source: TemplateSource;
  /** Required for an upload; an editor template has no archive. */
  fileObject?: string;
  format: string;
  tags?: string[];
  /** The master size an editor template starts at. */
  size?: TemplateSize;
}

/** An absent field is unchanged. */
export interface TemplateUpdate {
  name?: string;
  tags?: string[];
  format?: string;
  /** The exported bundle, written when an editor template is exported; never set for
   *  an uploaded archive. */
  fileObject?: string;
}

/** One uploaded archive: its object name and the result of the inspection. */
export interface TemplateArchive {
  fileName: string;
  fileObject: string;
  inspection: TemplateInspection;
}

/** Sends one template request and puts the template in the response into the list,
 *  marked until the stored data shows it. */
function useTemplateMutation<TVariables>(
  kind: PendingKind,
  send: (variables: TVariables) => Promise<unknown>,
  meta?: MutationMeta,
  onSuccess?: (template: Template) => void,
) {
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: brandHubQueryKey('templates', organizationId),
    kind,
    mutationFn: async (variables: TVariables) =>
      requireParsed(parseWriteResponse(await send(variables), 'template_id', parseTemplate), 'template'),
    rowOf: (template: Template) => template,
    meta,
    onSuccess,
  });
}

/** Stores one new template, uploaded or drawn in the editor. */
export function useCreateTemplate(meta?: MutationMeta) {
  return useTemplateMutation(
    'create',
    (template: TemplateCreate) =>
      apiClient.post(endpoints.brandHub.template.create(), {
        name: template.name,
        template_type: template.templateType,
        source: template.source,
        ...(template.fileObject !== undefined && { file_object: template.fileObject }),
        ...(template.size !== undefined && { size: template.size }),
        ...(template.tags !== undefined && { tags: template.tags }),
        format: template.format,
      }),
    meta,
  );
}

/** Changes the name, the tags, the format or the exported bundle of one template. */
export function useUpdateTemplate() {
  return useTemplateMutation(
    'update',
    ({ templateId, update }: { templateId: string; update: TemplateUpdate }) =>
      apiClient.put(endpoints.brandHub.template.update(templateId), {
        ...(update.name !== undefined && { name: update.name }),
        ...(update.tags !== undefined && { tags: update.tags }),
        ...(update.format !== undefined && { format: update.format }),
        ...(update.fileObject !== undefined && { file_object: update.fileObject }),
      }),
    { toastsError: false },
  );
}

/** Removes one template, then calls `onDeleted`, also when the caller has unmounted. */
export function useDeleteTemplate({ onDeleted }: { onDeleted?: () => void } = {}) {
  return useTemplateMutation(
    'delete',
    (templateId: string) => apiClient.delete(endpoints.brandHub.template.delete(templateId)),
    undefined,
    onDeleted,
  );
}

/** Uploads one exported bundle and writes its object name onto the template. */
export function useStoreTemplateBundle() {
  const upload = useUploadBrandHubFile({ folder: 'templates' });
  const update = useUpdateTemplate();
  return useMutation({
    mutationFn: async ({ templateId, file }: { templateId: string; file: File }) =>
      update.mutateAsync({ templateId, update: { fileObject: await upload.mutateAsync(file) } }),
    meta: { errorKey: 'brandHub.editor.bundleNotStored' },
  });
}

/** Uploads the archive to the templates folder, then gets its format from the backend. */
export function useUploadTemplateArchive() {
  const upload = useUploadBrandHubFile({ folder: 'templates' });

  return useMutation({
    mutationFn: async (file: File): Promise<TemplateArchive> => {
      const fileObject = await upload.mutateAsync(file);
      const body = await apiClient.post(endpoints.brandHub.template.inspect(), { file_object: fileObject });
      return { fileName: file.name, fileObject, inspection: parseTemplateInspection(readRecord(body)) };
    },
    meta: { toastsError: false },
  });
}

export type TemplatePreviewStatus = 'loading' | 'ready' | 'unavailable' | 'tooLarge' | 'noPage';

export interface TemplatePreviewState {
  status: TemplatePreviewStatus;
  /** The assembled document, only while `ready`. */
  html: string | null;
  /** The size the archive declares, for a template row that stored none. */
  size: TemplateSize | null;
}

const LOADING: TemplatePreviewState = { status: 'loading', html: null, size: null };
const UNAVAILABLE: TemplatePreviewState = { status: 'unavailable', html: null, size: null };

async function downloadAndAssemble(url: string): Promise<PreviewResult> {
  return assembleTemplatePreview(new Uint8Array(await apiClient.download(url, 'arrayBuffer')));
}

function stateOf(result: PreviewResult): TemplatePreviewState {
  if (result.ok) return { status: 'ready', html: result.html, size: result.size };
  return result.reason === 'unreadable' ? UNAVAILABLE : { status: result.reason, html: null, size: null };
}

/** Returns the query of the preview page of one template archive. */
function templatePreviewQuery(fileObject: string | null, url: string | null) {
  return queryOptions({
    queryKey: ['template-preview', fileObject] as const,
    staleTime: Infinity,
    retry: false,
    queryFn: url ? () => downloadAndAssemble(url) : skipToken,
  });
}

/** Downloads one template archive and assembles its self-contained preview page,
 *  cached by object name. */
export function useTemplatePreview(fileObject: string | null, enabled = true): TemplatePreviewState {
  const location = useObjectUrlQuery(enabled ? fileObject : null);
  const url = location.data ?? null;

  const query = useQuery(templatePreviewQuery(fileObject, url));

  const unresolved = location.isError || (location.isSuccess && url === null);
  if (unresolved || query.isError) return UNAVAILABLE;
  return query.data ? stateOf(query.data) : LOADING;
}
